import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LandingPageNav from '../../components/landingpage/LandingPageNav';
import Footer from '../../components/Footer/footer';
import ApplyFlowButton from '../LoanPrograms/ApplyFlowButton';


const brandGold = '#b79a59';

function CTAImageBanner({ image, heading, subheading, buttonText = 'Apply Now' }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: { xs: 220, sm: 260 },
        borderRadius: 2,
        overflow: 'hidden',
        backgroundImage: `linear-gradient(rgba(10,10,10,.45), rgba(10,10,10,.55)), url(${image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        my: { xs: 4, md: 6 },
      }}
      role="img"
      aria-label={heading}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: { xs: 2, sm: 4 },
        }}
      >
        <Box>
          <Typography variant="h3" component="h2" sx={{ color: 'common.white', mb: 1 }}>
            {heading}
          </Typography>
          {subheading && (
            <Typography variant="body1" sx={{ color: 'grey.100', mb: 2 }}>
              {subheading}
            </Typography>
          )}
          <ApplyFlowButton
            label={buttonText}
            size="large"
            sx={{ bgcolor: brandGold, '&:hover': { bgcolor: '#a2874b' } }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default function RefinancePage() {
  // Replace with your real asset paths
  const images = {
    hero: 'https://res.cloudinary.com/ddhrdxc7d/image/upload/v1763994545/Blog_Landing_Page_Website_Header_-_Refinance_xos3in.jpg',
    cta: 'https://res.cloudinary.com/ddhrdxc7d/image/upload/v1758823978/Jet_HomeLoans_Acrylic_Standoffs_-_Photography_1_ipxxuc.jpg',
  };

  return (
    <>
      <LandingPageNav />
      {/* clear fixed AppBar height */}
      <Box sx={(theme) => ({ ...theme.mixins.toolbar })} />


   {/* HERO */}
<Box
  sx={{
    position: 'relative',
    width: '100%',
    overflowX: 'hidden',
    backgroundImage: `url(${images.hero})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center top',
    minHeight: { xs: '55vh', md: '70vh' },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  }}
>
  {/* Text Content – EXACT alignment match */}
  <Box
    sx={{
      position: 'relative',
      zIndex: 2,
      ml: { xs: 2, sm: 4, md: 6 }, // ← identical spacing as Home + Purchase
      maxWidth: 600,
    }}
  >
    <Typography variant="heroHeadline" sx={{ color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
      Refinance
    </Typography>

    <Typography
      variant="heroSubheadline"
      sx={{
        color: '#fff',
        maxWidth: 600,
        mt: 1.5,
        textShadow: '0 2px 6px rgba(0,0,0,0.5)',
      }}
    >
      Home Equity is the cheapest form of money borrowed
    </Typography>
  </Box>
</Box>


      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* Main copy */}
          <Grid item xs={12} md={8}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              Refinance
            </Typography>

            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
              Are you ready to save on mortgage payments? Refinancing while interest rates are favorable offers an excellent
              opportunity to reduce your monthly payment. By converting your current fixed-rate loan or adjustable-rate
              mortgage (ARM), you could secure a lower rate and reduce your payment amount. If you’re paying private
              mortgage insurance (PMI), refinancing might allow you to eliminate that cost, depending on your home’s
              equity. Lowering both your interest rate and removing PMI can significantly reduce your long-term loan costs,
              freeing up more money for the things that matter most to you.
            </Typography>

            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
              Refinancing offers more than just lowering your monthly payment—it can also help you tap into your home
              equity for improvements, consolidate high-interest debt, or shorten your loan term. Whether you want to free
              up funds for a major purchase or optimize your finances, refinancing can be an effective solution. At Jet
              HomeLoans, we make the process simple and efficient with in-house underwriting, processing, and
              decision-making. Even if your original mortgage wasn’t with us, you can still refinance through Jet
              HomeLoans.
            </Typography>

            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
              Contact one of our mortgage experts today to explore how refinancing could benefit your financial goals.
            </Typography>

            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Keep in mind that refinancing may result in higher overall finance charges over the life of your loan.
            </Typography>
          </Grid>

          {/* Right rail: benefits + CTAs */}
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
              <CardContent>
                <Typography variant="h4" component="h2" sx={{ mb: 1 }}>
                  Why Refinance with Jet?
                </Typography>
                <List dense>
                  {[
                    'Lower your interest rate',
                    'Remove monthly PMI (with sufficient equity)',
                    'Cash-out options for projects or debt consolidation',
                    'Shorten your term to pay off faster',
                    'Fast decisions with in-house processing',
                  ].map((text) => (
                    <ListItem key={text} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon sx={{ color: brandGold }} />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={text} />
                    </ListItem>
                  ))}
                </List>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
  <ApplyFlowButton
    fullWidth
    size="medium"
    label="Start Application"
    sx={{ bgcolor: brandGold, '&:hover': { bgcolor: '#a2874b' } }}
  />
  <Button
    fullWidth
    variant="outlined"
    sx={{ borderColor: brandGold, color: brandGold, '&:hover': { borderColor: brandGold } }}
    href="/contact"
  >
    Talk to an Expert
  </Button>
</Box>

              </CardContent>
            </Card>

            <Card elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h4" component="h2" sx={{ mb: 1 }}>
                Estimate Your Savings
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Run quick scenarios to see how rate, term, or cash-out affects your payment.
              </Typography>
              <Button variant="text" href="/calculators" sx={{ color: brandGold }}>
                Open Mortgage Calculator →
              </Button>
            </Card>
          </Grid>
        </Grid>

        <CTAImageBanner
          image={images.cta}
          heading="Ready to Refinance?"
          subheading="Lower your monthly payment or tap your home equity—let’s find your best option."
          buttonText="Check Your Options"
        />
      </Container>

      <Footer />
    </>
  );
}
